<template>
  <div v-if="pending" class="cortex-tool-approval">
    <div class="cortex-tool-approval__rail">
      <i class="fas fa-shield-alt"></i>
    </div>
    <div class="cortex-tool-approval__body">
      <div class="cortex-tool-approval__top">
        <div>
          <div class="cortex-tool-approval__title">{{ $t('kortex.toolApprovalTitle') }}</div>
          <div class="cortex-tool-approval__subtitle">{{ $t('kortex.toolApprovalIntro') }}</div>
        </div>
      </div>

      <div class="cortex-tool-approval__calls">
        <div v-for="call in calls" :key="call.id" class="cortex-tool-approval__call">
          <div class="cortex-tool-approval__call-head">
            <div class="cortex-tool-approval__name">
              <i class="fas fa-tools"></i>
              <span>{{ call.name }}</span>
            </div>
            <div class="cortex-tool-approval__badges">
              <span class="cortex-tool-approval__badge" :class="'cortex-tool-approval__badge--' + risk(call)">
                {{ riskLabel(call) }}
              </span>
              <span class="cortex-tool-approval__badge cortex-tool-approval__badge--category">
                {{ categoryLabel(call) }}
              </span>
            </div>
          </div>
          <details class="cortex-tool-approval__args">
            <summary>{{ $t('kortex.toolApprovalArgs') }}</summary>
            <pre>{{ argsText(call) }}</pre>
          </details>
        </div>
      </div>

      <div class="cortex-tool-approval__hint">
        <i class="fas fa-info-circle"></i>
        <span>{{ $t('kortex.toolApprovalFullHint') }}</span>
      </div>

      <div class="cortex-tool-approval__actions">
        <button class="cortex-tool-approval__btn cortex-tool-approval__btn--ghost" :disabled="busy" @click="deny">
          {{ $t('kortex.toolApprovalDeny') }}
        </button>
        <div class="cortex-tool-approval__menu-wrap">
          <button
            class="cortex-tool-approval__btn cortex-tool-approval__btn--secondary"
            :disabled="busy"
            @click="toggleActionMenu"
          >
            <span>{{ $t('kortex.toolApprovalSessionOptions') }}</span>
            <i class="fas fa-chevron-down"></i>
          </button>
          <div v-if="actionMenuOpen" class="cortex-tool-approval__menu">
            <button class="cortex-tool-approval__menu-item" :disabled="busy" @click="approveTool">
              <i class="fas fa-check-circle"></i>
              <span>{{ allowToolLabel }}</span>
            </button>
            <button class="cortex-tool-approval__menu-item cortex-tool-approval__menu-item--warning" :disabled="busy" @click="approveFull">
              <i class="fas fa-bolt"></i>
              <span>{{ $t('kortex.toolApprovalFullAccess') }}</span>
            </button>
          </div>
        </div>
        <button class="cortex-tool-approval__btn cortex-tool-approval__btn--primary" :disabled="busy" @click="approveOnce">
          {{ $t('kortex.toolApprovalAllowOnce') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ToolApprovalCard',
  data: function () {
    return {
      actionMenuOpen: false,
      busy: false
    }
  },
  computed: {
    pending: function () {
      return this.$store.state.cortex.pendingToolApproval
    },
    calls: function () {
      return (this.pending && this.pending.calls) || []
    },
    uniqueToolNames: function () {
      var names = []
      this.calls.forEach(function (call) {
        if (call.name && names.indexOf(call.name) === -1) names.push(call.name)
      })
      return names
    },
    allowToolLabel: function () {
      if (this.uniqueToolNames.length > 1) return this.$t('kortex.toolApprovalAlwaysAllowTools')
      return this.$t('kortex.toolApprovalAlwaysAllowTool')
    }
  },
  methods: {
    risk: function (call) {
      return (call.policy && call.policy.risk) || 'medium'
    },
    riskLabel: function (call) {
      return this.$t('kortex.toolRisk' + this.capitalize(this.risk(call)))
    },
    categoryLabel: function (call) {
      var category = (call.policy && call.policy.category) || 'write'
      var suffix = category.split('_').map(this.capitalize).join('')
      return this.$t('kortex.toolCategory' + suffix)
    },
    capitalize: function (value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    },
    argsText: function (call) {
      if (!call.argumentsText || call.argumentsText === '{}') return this.$t('kortex.toolApprovalNoArgs')
      return call.argumentsText
    },
    approveOnce: function () {
      this.actionMenuOpen = false
      this.submit('cortex/approvePendingTools', { mode: 'allow_once' })
    },
    approveTool: function () {
      this.actionMenuOpen = false
      this.submit('cortex/approvePendingTools', { mode: 'allow_tool', toolNames: this.uniqueToolNames })
    },
    approveFull: function () {
      this.actionMenuOpen = false
      this.submit('cortex/approvePendingTools', { mode: 'full' })
    },
    deny: function () {
      this.actionMenuOpen = false
      this.submit('cortex/denyPendingTools')
    },
    toggleActionMenu: function () {
      if (this.busy) return
      this.actionMenuOpen = !this.actionMenuOpen
    },
    submit: function (action, payload) {
      if (this.busy) return
      var self = this
      this.busy = true
      return this.$store.dispatch(action, payload).finally(function () {
        self.busy = false
      })
    }
  }
}
</script>

<style>
.cortex-tool-approval {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin: 12px 0 14px 44px;
  max-width: 680px;
  width: calc(100% - 52px);
}

.cortex-tool-approval__rail {
  align-items: center;
  background: rgba(255, 193, 7, 0.12);
  border: 1px solid rgba(255, 193, 7, 0.24);
  border-radius: 8px;
  color: #ffd15c;
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
}

.cortex-tool-approval__body {
  background: rgba(20, 22, 31, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.86);
  flex: 1;
  min-width: 0;
  padding: 12px;
}

.cortex-tool-approval__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.cortex-tool-approval__title {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.cortex-tool-approval__subtitle,
.cortex-tool-approval__hint {
  color: rgba(255, 255, 255, 0.58);
  font-size: 12px;
  line-height: 1.4;
}

.cortex-tool-approval__subtitle {
  margin-top: 2px;
}

.cortex-tool-approval__calls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.cortex-tool-approval__call {
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 7px;
  padding: 9px;
}

.cortex-tool-approval__call-head {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.cortex-tool-approval__name {
  align-items: center;
  display: flex;
  gap: 7px;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  min-width: 0;
}

.cortex-tool-approval__name span {
  overflow-wrap: anywhere;
}

.cortex-tool-approval__badges {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: flex-end;
}

.cortex-tool-approval__badge {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 7px;
}

.cortex-tool-approval__badge--low {
  border-color: rgba(40, 167, 69, 0.35);
  color: #63d17b;
}

.cortex-tool-approval__badge--medium {
  border-color: rgba(255, 193, 7, 0.35);
  color: #ffd15c;
}

.cortex-tool-approval__badge--high {
  border-color: rgba(220, 53, 69, 0.4);
  color: #ff7b8a;
}

.cortex-tool-approval__badge--category {
  background: rgba(255, 255, 255, 0.04);
}

.cortex-tool-approval__args {
  color: rgba(255, 255, 255, 0.58);
  font-size: 11px;
  margin-top: 8px;
}

.cortex-tool-approval__args summary {
  cursor: pointer;
  font-weight: 700;
}

.cortex-tool-approval__args pre {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 11px;
  line-height: 1.45;
  margin: 7px 0 0;
  max-height: 160px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

.cortex-tool-approval__hint {
  align-items: flex-start;
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

.cortex-tool-approval__hint i {
  color: #63d17b;
  margin-top: 2px;
}

.cortex-tool-approval__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  justify-content: flex-end;
  margin-top: 12px;
}

.cortex-tool-approval__btn {
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 7px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  min-height: 32px;
  padding: 7px 10px;
}

.cortex-tool-approval__btn:disabled {
  cursor: default;
  opacity: 0.55;
}

.cortex-tool-approval__btn--ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.64);
}

.cortex-tool-approval__btn--primary {
  background: rgba(108, 128, 255, 0.88);
  border-color: rgba(108, 128, 255, 0.88);
  color: white;
}

.cortex-tool-approval__btn--secondary {
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.74);
  display: flex;
  gap: 7px;
}

.cortex-tool-approval__btn--warning {
  background: rgba(255, 193, 7, 0.9);
  border-color: rgba(255, 193, 7, 0.9);
  color: #1f2937;
}

.cortex-tool-approval__menu-wrap {
  position: relative;
}

.cortex-tool-approval__menu {
  background: #171a24;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  bottom: calc(100% + 6px);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.42);
  min-width: 240px;
  overflow: hidden;
  padding: 5px;
  position: absolute;
  right: 0;
  z-index: 20;
}

.cortex-tool-approval__menu-item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.78);
  cursor: pointer;
  display: flex;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  padding: 9px;
  text-align: left;
  width: 100%;
}

.cortex-tool-approval__menu-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.cortex-tool-approval__menu-item:disabled {
  cursor: default;
  opacity: 0.55;
}

.cortex-tool-approval__menu-item i {
  color: #63d17b;
  flex: 0 0 14px;
}

.cortex-tool-approval__menu-item--warning i {
  color: #ffd15c;
}

@media (max-width: 575.98px) {
  .cortex-tool-approval {
    margin-left: 0;
    width: 100%;
  }

  .cortex-tool-approval__rail {
    display: none;
  }

  .cortex-tool-approval__call-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .cortex-tool-approval__badges,
  .cortex-tool-approval__actions {
    justify-content: flex-start;
  }

  .cortex-tool-approval__menu-wrap,
  .cortex-tool-approval__btn {
    width: 100%;
  }

  .cortex-tool-approval__btn {
    justify-content: center;
  }

  .cortex-tool-approval__menu {
    left: 0;
    min-width: 0;
    right: 0;
  }
}
</style>
